// src/auth/ProtectedRoute.jsx
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';

/**
 * Protects routes by checking authentication, roles, and/or features
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} [props.requiredRole] - Role required to access this route (e.g., 'admin')
 * @param {string} [props.requiredFeature] - Feature required to access this route (e.g., 'dashboard')
 *
 * @example
 * // Authentication only
 * <ProtectedRoute>
 *   <HomePage />
 * </ProtectedRoute>
 *
 * @example
 * // Role-based access (admin routes)
 * <ProtectedRoute requiredRole="admin">
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * @example
 * // Feature-based access (MFE access)
 * <ProtectedRoute requiredFeature="dashboard">
 *   <Dashboard />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({
                            children,
                            requiredRole = null,
                            requiredFeature = null
                        }) => {
    const { user, loading, hasFeature, hasPermission } = useAuth();
    const location = useLocation();

    // Show loader while checking auth
    if (loading) {
        return <PageLoader />;
    }

    // Redirect to login if not authenticated
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check role access (e.g., admin routes)
    if (requiredRole && !hasPermission(requiredRole)) {
        return <Navigate to="/unauthorized" replace />;
    }

    // Check feature access (e.g., MFE access)
    if (requiredFeature && !hasFeature(requiredFeature)) {
        return <Navigate to="/unauthorized" replace />;
    }

    return children;
};

export default ProtectedRoute;